import SlsTracker from '@aliyun-sls/web-track-browser';
import CreateStsPlugin from '@aliyun-sls/web-sts-plugin';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { storage } from '../storageOperateApi';
import { LOCAL_STORAGE_KEY } from '../../constants';
const { SEQUENCE, DEFAULT_DATA_TRACK } = LOCAL_STORAGE_KEY || {};

dayjs.extend(timezone);
dayjs.extend(utc);

const BaseOpts = {
  host: 'cn-hangzhou.log.aliyuncs.com', // 所在地域的服务入口
  project: 'comein-web', // Project名称
  // time: 3, // 发送日志的时间间隔，默认是10秒
  // count: 10, // 发送日志的数量大小，默认是10条
  source: location.href.split('?')[0].slice(0, 128)
};

const StsOpt = {
  accessKeyId: '',
  accessKeySecret: '',
  securityToken: '',
  refreshSTSToken: async () => {
    const url = ENV_DATA.VITE_PHP_HOST;
    const response = await fetch(`${url}`, {
      method: 'POST',
      headers: {
        app: 'json',
        mod: 'sts',
        act: 'get-sls-credentials',
        os: 'web',
        c: 'pc',
        b: '4.2.0800',
        'Content-Type': 'application/json'
      }
    });
    const res = await response.json();
    StsOpt.accessKeyId = res.data.accessKeyId;
    StsOpt.accessKeySecret = res.data.accessKeySecret;
    StsOpt.securityToken = res.data.securityToken;
  }
};

const stsPlugin = CreateStsPlugin(StsOpt);

const getTimeZone = () => {
  return dayjs.tz.guess();
};

/**
 * 生成GUID（全局唯一标识符）
 * @returns {string} GUID字符串
 */

export const generateGUID = function () {
  // 优先使用现代浏览器的crypto.randomUUID()
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  // 降级方案：使用UUID v4算法
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

// 事件类型枚举
export const EVENT_TYPE = {
  // 基础交互事件
  CLICK: 'click', // 用户点击事件
  HOVER: 'hover', // 用户悬停事件
  SCROLL: 'scroll', // 用户滚动事件
  NAVIGATE: 'navigate', // 用户导航事件

  // 表单相关事件
  SUBMIT: 'submit', // 表单提交事件
  INPUT: 'input', // 输入事件
  FOCUS: 'focus', // 聚焦事件
  BLUR: 'blur', // 失焦事件
  CHANGE: 'change', // 值改变事件
  SELECT: 'select', // 选择事件
  ENTER: 'enter', // 回车事件

  // 搜索和操作
  SEARCH: 'search', // 搜索事件
  FILTER: 'filter', // 筛选事件
  SORT: 'sort', // 排序事件

  // 文件操作
  UPLOAD: 'upload', // 上传事件
  DOWNLOAD: 'download', // 下载事件
  EXPORT: 'export', // 导出事件
  IMPORT: 'import', // 导入事件

  // 页面相关
  PAGE_VIEW: 'page_view', // 页面浏览
  PAGE_LEAVE: 'page_leave', // 页面离开
  TAB_SWITCH: 'tab_switch', // 标签页切换

  // 媒体相关
  PLAY: 'play', // 播放事件
  PAUSE: 'pause', // 暂停事件

  // 交互操作
  DRAG: 'drag', // 拖拽事件
  DROP: 'drop', // 放置事件
  COPY: 'copy', // 复制事件
  PASTE: 'paste', // 粘贴事件
  RESIZE: 'resize', // 窗口调整大小

  // 操作状态
  CANCEL: 'cancel', // 取消操作
  CONFIRM: 'confirm', // 确认操作
  CLOSE: 'close', // 关闭操作
  EXPAND: 'expand', // 展开操作
  COLLAPSE: 'collapse' // 收起操作
};

export const LoggerStore = {
  XHR: 'xhr',
  WEBSOCKET: 'websocket',
  CONSOLE: 'console',
  BEHAVIOR: 'behavior',
  ERROR: 'error'
};

class Logger {
  constructor() {
    this.bolSend =
      /\.finenter\.ai/i.test(location.hostname.toLowerCase()) ||
      /\.comein\.cn/i.test(location.hostname.toLowerCase()) ||
      localStorage.getItem('logger');

    this.trackers = {};

    this.roadshowId = '';

    // 获取用户信息，uid/token
    this.getUserInfo = () => {};

    // 设置公共数据
    this.getExtra = () => {};

    this.i18n = null;

    this.EVENT_TYPE = EVENT_TYPE;

    // install 阶段初始化 STS 的 Promise（send 中等待）
    this.stsReadyPromise = null;
  }

  createLoggerId(id) {
    const loggerId = id || sessionStorage.getItem('loggerId') || generateGUID();
    sessionStorage.setItem('loggerId', loggerId);
    return loggerId;
  }

  createTracker(key, project = '') {
    BaseOpts.project = project || BaseOpts.project;
    const tracker = new SlsTracker({
      ...BaseOpts,
      topic: key,
      logstore: `${this.logstore}-${key}`
    });

    tracker.useStsPlugin(stsPlugin);

    this.trackers[key] = tracker;
  }

  createDefaultData() {
    const userInfo = this.getUserInfo();
    const extra = this.getExtra() || {};

    // 收集额外的环境信息 改变时才上传
    const localDefaultData = JSON.parse(storage.get(DEFAULT_DATA_TRACK) || '{}');
    const data = {
      locale: this.i18n?.locale,
      language: navigator.language,
      width: screen.width,
      height: screen.height,
      innerHeight: window.innerHeight,
      innerWidth: window.innerWidth
    };
    Object.keys(data).forEach((key) => {
      if (data[key] === localDefaultData[key]) {
        delete data[key];
      } else {
        localDefaultData[key] = data[key];
      }
    });
    storage.set(DEFAULT_DATA_TRACK, JSON.stringify(localDefaultData), false, false, false);

    const payload = {
      id: generateGUID(),
      loggerId: this.loggerId,
      uid: userInfo?.uid || '',
      token: userInfo?.token || userInfo?.webtoken || '',
      timestamp: Date.now(),
      userAgent: navigator.userAgent,
      href: location.href,
      isRefresh: performance.getEntriesByType('navigation')[0]?.type === 'reload',
      zone: getTimeZone(),
      extra: {
        ...data,
        ...extra
      }
    };
    // 清理 undefined 和 null 字段
    Object.keys(payload).forEach((key) => {
      if (payload[key] === undefined || payload[key] === null) {
        delete payload[key];
      }
    });
    return payload;
  }

  xhr = function (
    { api, url, method, headers, data = '', params = '', response, responseHeaders, status, error = '', extra = {} },
    immediate = true
  ) {
    const payload = {
      api,
      url,
      method,
      headers,
      data,
      params,
      response,
      responseHeaders,
      status,
      error,
      extra
    };
    const phpApi = `${headers?.app || data?.app}_${headers?.mod || data?.mod}_${headers?.act || data?.act}`;
    payload.api = !api ? (phpApi === 'undefined_undefined_undefined' ? url : '') : '';

    Object.keys(payload).forEach((key) => {
      if (
        payload[key] === undefined ||
        payload[key] === null ||
        (typeof payload[key] === 'object' && Object.keys(payload[key]).length === 0)
      ) {
        delete payload[key];
      }
    });
    this._sendInternal(LoggerStore.XHR, payload, immediate);
  };

  websocket = function (
    { request = '', response = '', responseBody = '', status = 'success', error = '', extra = {} },
    immediate = false
  ) {
    this._sendInternal(
      LoggerStore.WEBSOCKET,
      {
        request,
        response,
        responseBody,
        status,
        error,
        extra
      },
      immediate
    );
  };

  behavior = function ({ action, target = {}, extra = {} }, immediate = true) {
    // 所有行为都增加sequence
    storage.set(SEQUENCE, Number(storage.get(SEQUENCE)) + 1, false, false, false);
    this._sendInternal(
      LoggerStore.BEHAVIOR,
      {
        action,
        target,
        extra,
        sequence: storage.get(SEQUENCE) || 0
      },
      immediate
    );
  };

  console = function ({ component, level, message, extra = {}, ...other }, immediate = true) {
    // 如果没有传入 component，尝试从调用栈自动获取
    if (!component) {
      try {
        const stack = new Error().stack;
        // 解析堆栈获取调用位置的文件名
        const match = stack.split('\n')[2]?.match(/([^/\\]+)\.vue/);

        component = match ? match[1] : 'Unknown';
      } catch (e) {
        console.error('Error in console handler:', e);
        component = 'Unknown';
      }
    }

    this._sendInternal(
      LoggerStore.CONSOLE,
      {
        component,
        level,
        message,
        extra,
        ...other
      },
      immediate
    );
  };

  error = function ({ type, message, filename, lineno, colno, stack, extra }, immediate = true) {
    this._sendInternal(
      LoggerStore.ERROR,
      {
        type, // 错误类型：js_error, vue_error, promise_rejection, resource_error, network_error
        message, // 错误消息
        filename, // 出错文件
        lineno, // 出错行号
        colno, // 出错列号
        stack, // 错误堆栈
        extra
      },
      immediate
    );
  };

  // 首次 send 触发一次 STS 刷新（无重试）；其他并发 send 等待完成后再发送
  send = async function (key, data) {
    return this._sendInternal(key, data, false);
  };

  sendImmediate = async function (key, data) {
    return this._sendInternal(key, data, true);
  };

  _sendInternal = async function (key, data, immediate = false) {
    if (!this.bolSend) {
      return;
    }

    const tracker = this.trackers[key];
    if (!tracker) {
      return;
    }

    // 等待 install 阶段的 STS 初始化（若存在）
    if (this.stsReadyPromise) {
      try {
        await this.stsReadyPromise;
      } catch (e) {
        console.error('Error in stsReadyPromise:', e);
        // 初始化失败则本次跳过，避免 400
        return;
      }
    }

    // 初始化后仍无凭据，跳过（不在 send 里触发刷新）
    if (!(StsOpt.accessKeyId && StsOpt.accessKeySecret)) {
      return;
    }

    tracker.getOpt().source = location.href.split('?')[0].slice(0, 128);

    const defaultData = this.createDefaultData();
    const extra = Object.assign({}, defaultData.extra, data.extra);
    const payload = {
      ...defaultData,
      ...data,
      extra
    };

    Object.keys(payload).forEach((key) => {
      if (
        payload[key] === undefined ||
        payload[key] === null ||
        (typeof payload[key] === 'object' && Object.keys(payload[key]).length === 0)
      ) {
        delete payload[key];
      }
    });

    if (immediate && typeof tracker.sendImmediate === 'function') {
      tracker.sendImmediate(payload);
    } else {
      tracker.send(payload);
    }
  };

  createErrorListener(Vue) {
    const that = this;
    /**
     * 错误采样器
     * 避免相同错误重复上报
     */
    const errorSampler = {
      cache: new Map(),
      maxCacheSize: 50,

      // 生成错误的唯一标识
      generateErrorKey(message, filename, lineno, colno) {
        return `${message}-${filename}-${lineno}-${colno}`;
      },

      // 检查是否应该采样该错误
      shouldSample(message, filename, lineno, colno) {
        const key = this.generateErrorKey(message, filename, lineno, colno);
        const now = Date.now();
        const lastTime = this.cache.get(key);

        // 如果5分钟内出现过相同错误，则不再上报
        if (lastTime && now - lastTime < 5 * 60 * 1000) {
          return false;
        }

        // 更新缓存
        this.cache.set(key, now);

        // 清理过期缓存
        if (this.cache.size > this.maxCacheSize) {
          this.cleanCache();
        }

        return true;
      },

      // 清理过期缓存
      cleanCache() {
        const now = Date.now();
        const expireTime = 10 * 60 * 1000; // 10分钟

        for (const [key, time] of this.cache.entries()) {
          if (now - time > expireTime) {
            this.cache.delete(key);
          }
        }
      }
    };

    /**
     * Vue 错误处理器
     * 捕获 Vue 实例中的错误
     */
    Vue.config.errorHandler = (err, instance, info) => {
      console.error('Vue error:', err, info);

      const message = err?.message || 'Unknown error';
      const stack = err?.stack || '';

      // 采样控制
      if (!errorSampler.shouldSample(message, 'vue-component', 0, 0)) {
        return;
      }

      // 获取组件信息
      let componentTree = '';

      if (instance) {
        // 构建组件树路径
        let current = instance;
        const path = [];
        while (current) {
          const name = current.$options.name || current.$options._componentTag;
          if (name) {
            path.unshift(name);
          }
          current = current.$parent;
        }
        componentTree = path.join(' > ');
      }

      let lineno = 0;
      let colno = 0;
      if (stack) {
        // 正则匹配堆栈中的行号和列号（适配不同浏览器/Node.js格式）
        const stackLineRegex = /\((.+?):(\d+):(\d+)\)|at (.+?):(\d+):(\d+)/;
        const match = stackLineRegex.exec(stack);

        if (match) {
          // 匹配分组处理（不同格式分组位置不同）
          lineno = match[2] || match[5] ? parseInt(match[2] || match[5], 10) : 0;
          colno = match[3] || match[6] ? parseInt(match[3] || match[6], 10) : 0;
        }
      }

      // 上报错误
      that.error({
        type: 'vue_error',
        message,
        filename: componentTree,
        lineno,
        colno,
        stack
      });
    };

    /**
     * Promise 未捕获拒绝处理器
     * 捕获未被 catch 的 Promise 拒绝
     */
    // 提前保存上报实例，避免上下文丢失
    const errorReporter = that.error ? that.error.bind(that) : () => {};

    window.addEventListener('unhandledrejection', (event) => {
      try {
        console.error('Unhandled promise rejection:', event.reason);

        let message = 'Unknown promise rejection';
        let stack = '';

        if (event.reason) {
          if (typeof event.reason === 'string') {
            message = event.reason;
          } else if (event.reason instanceof Error) {
            message = event.reason.message || 'Unknown error';
            stack = event.reason.stack || '';
          } else {
            try {
              message = JSON.stringify(event.reason, (key, value) => {
                if (typeof value === 'function' || value === null) return undefined;
                return value;
              });
            } catch (e) {
              console.error('Error in unhandledrejection handler:', e);
              message = `[Non-serializable reason]: ${String(event.reason)}`;
            }
          }
        }

        // 添加前缀标识
        message = `[Promise Rejection]: ${message}`;

        // 采样控制
        if (!errorSampler?.shouldSample(message, 'promise', 0, 0)) {
          return;
        }

        // 解析堆栈获取位置信息
        let filename = 'promise';
        let lineno = 0;
        let colno = 0;
        if (stack) {
          const stackLineRegex = /\((.+?):(\d+):(\d+)\)|at (.+?):(\d+):(\d+)/;
          const match = stackLineRegex.exec(stack);
          if (match) {
            filename = match[1] || match[4] || filename;
            lineno = parseInt(match[2] || match[5], 10) || 0;
            colno = parseInt(match[3] || match[6], 10) || 0;
          }
        }

        // 上报错误（含额外信息）
        errorReporter({
          type: 'promise_rejection',
          message,
          filename,
          lineno,
          colno,
          stack,
          extra: {
            promiseConstructor: event.promise?.constructor?.name,
            timestamp: Date.now()
          }
        });
      } catch (handleError) {
        console.error('Error in unhandledrejection handler:', handleError);
      } finally {
        // 确保阻止默认行为
        event.preventDefault();
      }
    });

    /**
     * JavaScript 运行时错误处理器
     * 捕获全局 JavaScript 错误
     */
    window.onerror = function (message, source, lineno, colno, error) {
      console.error('JavaScript error:', message, source, lineno, colno, error);

      // 采样控制
      if (!errorSampler.shouldSample(message, source, lineno, colno)) {
        return false;
      }

      // 上报错误
      that.error({
        type: 'js_error',
        message: message || error?.message || 'Unknown error',
        filename: source || 'unknown',
        lineno: lineno || 0,
        colno: colno || 0,
        stack: error?.stack || ''
      });

      // 返回 false 表示不阻止默认行为
      return false;
    };

    /**
     * 资源加载错误处理器
     * 捕获静态资源加载失败的错误
     */
    window.addEventListener(
      'error',
      (event) => {
        const target = event.target;

        // 只处理资源加载错误
        if (
          target !== window &&
          (target.tagName === 'IMG' ||
            target.tagName === 'SCRIPT' ||
            target.tagName === 'LINK' ||
            target.tagName === 'AUDIO' ||
            target.tagName === 'VIDEO')
        ) {
          const resourceUrl = target.src || target.href || 'unknown';

          // 采样控制
          if (!errorSampler.shouldSample('Resource load failed', resourceUrl, 0, 0)) {
            return;
          }

          // 上报错误
          that.error({
            type: 'resource_error',
            message: `Failed to load ${target.tagName.toLowerCase()}: ${resourceUrl}`,
            filename: resourceUrl,
            lineno: 0,
            colno: 0,
            stack: ''
          });
        }
      },
      true
    );

    /**
     * 空白页监测
     * 检测页面是否加载为空白页
     */
    window.addEventListener('DOMContentLoaded', function () {
      setTimeout(function () {
        if (document.body.innerHTML === '') {
          // 上报到监控系统
          that.console({
            level: 0,
            component: 'page_DOMContentLoaded',
            message: '页面加载为空白页'
          });
        }
      }, 3000); // 3秒后检查页面是否为空
    });
  }

  createDirective(Vue) {
    /**
     * 获取元素的完整HTML信息
     */
    const getElementInfo = function (element) {
      if (!element || element === document.body) {
        return 'body';
      }

      // 返回完整的HTML结构，但清理一些可能的敏感信息
      return cleanElementHTML(element.outerHTML);
    };

    /**
     * 清理元素HTML，移除可能的敏感信息
     */
    const cleanElementHTML = function (html) {
      // 替换可能的敏感属性值
      return (
        html
          // 移除可能的token或敏感数据属性
          .replace(/data-token="[^"]*"/g, 'data-token="***"')
          .replace(/data-key="[^"]*"/g, 'data-key="***"')
          // 限制长度，避免过长的HTML
          .substring(0, 1000)
      );
    };

    // 处理v-logger指令点击事件（纯日志记录）
    const handleLoggerClick = (el, binding, vnode) => {
      const elementInfo = getElementInfo(el); // 现在返回完整HTML
      if (typeof binding.value === 'function') {
        this.behavior({
          action: EVENT_TYPE.CLICK,
          target: {
            el: elementInfo,
            methods: binding?.value?.name,
            component: vnode?.ctx?.ctx?.$options?.name
          }
        });
      } else if (
        typeof binding.value === 'object' &&
        binding.value?.handler &&
        typeof binding.value.handler === 'function'
      ) {
        this.behavior({
          action: EVENT_TYPE.CLICK,
          target: {
            el: elementInfo,
            component: vnode?.ctx?.ctx?.$options?.name,
            ...(binding.value?.payload || {})
          }
        });
      }
    };

    // 注册Vue指令 v-logger-click
    Vue.directive('loggerClick', {
      mounted: (el, binding, vnode) => {
        // 保存当前的 binding 与 vnode，便于后续在 handler 中始终使用最新值
        el._loggerBinding = binding;
        el._loggerVnode = vnode;

        el._handleLoggerClick = function (event) {
          const currentBinding = el._loggerBinding || binding;
          const currentVnode = el._loggerVnode || vnode;

          // stop 修饰符
          if (currentBinding.modifiers && currentBinding.modifiers.stop) {
            event.stopPropagation();
          }

          // prevent 修饰符
          if (currentBinding.modifiers && currentBinding.modifiers.prevent) {
            event.preventDefault();
          }

          handleLoggerClick(el, currentBinding, currentVnode);

          if (typeof currentBinding.value === 'function') {
            currentBinding.value();
          } else if (
            typeof currentBinding.value === 'object' &&
            currentBinding.value?.handler &&
            typeof currentBinding.value.handler === 'function'
          ) {
            currentBinding.value.handler();
          }
        };

        // 添加点击事件监听
        el.addEventListener('click', el._handleLoggerClick);
      },

      // 在绑定值或修饰符变化时更新到元素上，确保点击回调使用最新函数
      update: (el, binding, vnode) => {
        el._loggerBinding = binding;
        el._loggerVnode = vnode;
      },

      beforeUnmount: (el) => {
        el.removeEventListener('click', el._handleLoggerClick);
        delete el._handleLoggerClick;
        delete el._loggerBinding;
        delete el._loggerVnode;
      }
    });
  }
}

const slsLogger = new Logger();

slsLogger.install = function (
  Vue,
  { isBuild = true, logstore, loggerId = '', i18n = null, getUserInfo = () => {}, bolSend = false }
) {
  this.isBuild = isBuild;

  this.logstore = logstore;

  this.loggerId = this.createLoggerId(loggerId);

  this.i18n = i18n;

  this.getUserInfo = getUserInfo;

  this.bolSend = this.bolSend || bolSend;

  BaseOpts.project = isBuild ? 'comein-web' : 'comein-web-dev';

  // 安装时初始化 STS，send 中仅等待其完成
  this.stsReadyPromise = StsOpt.refreshSTSToken().catch(() => {});

  Object.values(LoggerStore).forEach((key) => {
    this.createTracker(key);
  });

  this.createErrorListener(Vue);

  this.createDirective(Vue);
};

export default slsLogger;

export class Console {
  constructor(component) {
    this.component = component;
    this.console = console;
  }

  debug(...args) {
    this.logger('debug', ...args);
  }

  log(...args) {
    this.logger('log', ...args);
  }

  info(...args) {
    this.logger('info', ...args);
  }

  warn(...args) {
    this.logger('warn', ...args);
  }

  error(...args) {
    this.logger('error', ...args);
  }

  logger(level, ...args) {
    // 生产环境不输出到控制台
    !slsLogger.isBuild && this.console[level] && this.console[level](`[${this.component}]`, ...args);

    // 生产环境上报日志
    if (!['debug', 'log'].includes(level)) {
      const lastArg = args[args.length - 1];
      const hasStructuredExtra =
        args.length > 1 &&
        lastArg &&
        typeof lastArg === 'object' &&
        !Array.isArray(lastArg) &&
        !(lastArg instanceof Error);
      const structuredExtra = hasStructuredExtra ? { ...lastArg } : {};
      // 兼容旧查询继续保留 message 数组，同时把末尾对象复制到 extra，支持按 sessionID 等字段结构化检索
      if (structuredExtra.sessionId && !structuredExtra.sessionID) {
        structuredExtra.sessionID = structuredExtra.sessionId;
      }
      slsLogger.console({ component: this.component, level, message: args, extra: structuredExtra });
    }
  }
}

const httpRequestMap = new Map();

window.addEventListener('httpLoadStart', (e) => {
  httpRequestMap.set(e.detail?.requestId, e.detail);
});

window.addEventListener('httpLoadEnd', (e) => {
  if (httpRequestMap.has(e.detail?.requestId)) {
    // 计算耗时
    const startDetail = httpRequestMap.get(e.detail?.requestId);
    const duration = e.detail?.endTime - startDetail.startTime;
    console.log(`httpLoadEnd: HTTP Request to ${e.detail?.api} took ${duration} ms`);
    // 上报日志
    slsLogger.xhr({
      ...e.detail,
      extra: {
        duration
      }
    });

    // 删除请求记录
    httpRequestMap.delete(e.detail?.requestId);
  }
});
